import type { API } from 'chessboard/api/config/types';
import { Core } from 'chessboard/core/config/types';
import type { Move } from 'chessboard/model/move/types';
import { tryMovingForward } from 'chessboard/api/model/try-moving-forward';
import type JCE from '@chesscom/js-chess-engine';

/**
 * Create the moveForward method.
 */
export function createMoveForward(context: API.Context) {
  return function moveForward(
    followContinuationsOrMoveForwardParams?: Move.Forward | boolean,
    mv?: JCE.AnyMoveType,
  ): API.Response {
    const { respond, api, game } = context;

    const { move, followContinuations, steps } = getMoveForwardParameters(
      followContinuationsOrMoveForwardParams,
      mv,
    );

    if (steps < 1) {
      return null;
    }

    const { firstStepData, success, endIds } = tryMovingForward({
      context,
      move,
      followContinuations,
      steps,
    });

    // return early if only 1 step made or first step fails
    if (steps === 1 || firstStepData?.plyDiff !== 1) {
      game.selectPosition(endIds.move, endIds.line);

      return respond(Core.APIEvents.MoveForward, firstStepData);
    }

    // multiple steps failed
    if (!success) {
      return respond(Core.APIEvents.MoveForward, null);
    }

    // multiple steps succeeded
    return api.selectNode(endIds.line, endIds.move);
  };
}

/**
 * Parse move forward parameters
 */
function getMoveForwardParameters(
  followContinuationsOrMoveForwardParams?: Move.Forward | boolean,
  mv?: JCE.AnyMoveType,
) {
  let moveForwardParams: Move.Forward = {};
  let follow;

  if (typeof followContinuationsOrMoveForwardParams === 'object') {
    moveForwardParams = followContinuationsOrMoveForwardParams;
  } else {
    follow = followContinuationsOrMoveForwardParams;
  }

  const { move, followContinuations, steps } = moveForwardParams;

  return {
    move: move ?? mv,
    followContinuations: followContinuations ?? follow ?? true,
    steps: steps ?? 1,
  };
}
